'use client';

import React from 'react';
import Image, { ImageProps } from 'next/image';

// Hostnames whose banner_image URLs we trust to flow through next/image
// (i.e. Next will fetch + re-encode them to AVIF/WebP via the optimizer).
// Anything NOT on this list — typically a producer who pasted an imgur,
// dropbox or random URL — falls back to a plain <img>, so producer
// content keeps working without us having to allowlist every host in
// `next.config.mjs/images.remotePatterns`.
//
// Keep this list in sync with `next.config.mjs` `remotePatterns`.
const OPTIMIZED_HOST_SUFFIXES = [
  'storage.googleapis.com',
  'firebasestorage.googleapis.com',
  'firebasestorage.app',
  'appspot.com',
  'res.cloudinary.com',
  'encrypted-tbn0.gstatic.com',
  'cdn.avantti.io',
];

const isOptimizable = (src: string): boolean => {
  if (src.startsWith('/')) return true; // local /public/* asset
  try {
    const url = new URL(src);
    if (url.protocol !== 'https:') return false;
    return OPTIMIZED_HOST_SUFFIXES.some(
      (suffix) => url.hostname === suffix || url.hostname.endsWith(`.${suffix}`),
    );
  } catch {
    return false;
  }
};

type Props = Omit<ImageProps, 'src'> & {
  src: string;
};

const EventBannerImage: React.FC<Props> = ({
  src, alt, sizes, priority, className, ...rest
}) => {
  if (isOptimizable(src)) {
    return (
      <Image
        src={src}
        alt={alt}
        fill
        sizes={sizes}
        priority={priority}
        className={className}
        {...rest}
      />
    );
  }
  // Untrusted host — pass through. We still want lazy decode and a
  // sensible loading hint; the parent container provides the aspect
  // ratio so `object-cover` is enough.
  const loading = priority ? 'eager' : 'lazy';
  return (
    <img
      src={src}
      alt={alt as string}
      loading={loading}
      decoding="async"
      className={`absolute inset-0 w-full h-full ${className ?? ''}`}
    />
  );
};

export default EventBannerImage;
